<template>
  <section class="benefits module">
    <div class="container">
      <div class="benefits-head">
        <div
          class="headline h3"
          v-html="data.benefits_headline"></div>
        <div
          class="text editor-formatting"
          v-html="data.benefits_text"></div>
      </div>
    </div>
    <div
      ref="trigger"
      class="benefits-list">
      <div class="hex-outline-container">
        <div
          ref="hexOne"
          class="hex-outline one"></div>
        <div
          ref="hexTwo"
          class="hex-outline two"></div>
        <div
          ref="hexThree"
          class="hex-outline three"></div>
      </div>
      <Splide
        :options="options"
        :has-track="false"
        @splide:moved="sliderMoved">
        <SplideTrack>
          <SplideSlide
            v-for="(item, index) in data.benefits_list"
            :key="index">
            <BenefitItem :item="item" />
          </SplideSlide>
        </SplideTrack>
        <div
          v-if="data.benefits_list.length > 1"
          class="splide__arrows">
          <button class="splide__arrow splide__arrow--prev"></button>
          <div class="arrow-count h4">
            <span>{{ active }}</span
            ><span> / </span><span>{{ data.benefits_list.length }}</span>
          </div>
          <button class="splide__arrow splide__arrow--next"></button>
        </div>
      </Splide>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "GeneralContentComponent",
    "CLASSNAME": "benefits",
    "URL": "http://insitro.local/test/"
   }
  #ENDMETA */
import BenefitItem from '@/components/modules/Benefits/BenefitItem.vue'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { gsap, Linear } from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

import { Splide, SplideTrack, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/vue-splide/css/core'

gsap.registerPlugin(ScrollTrigger)

defineProps(['data'])

const options = {
  pagination: false,
  arrows: true,
}

const hexOne = ref(null)
const hexTwo = ref(null)
const hexThree = ref(null)
const trigger = ref(null)
const active = ref(1)

let tl

const sliderMoved = (splide, prev, next, dest) => {
  active.value = dest + 1
}

onMounted(() => {
  tl = gsap.timeline({
    scrollTrigger: {
      trigger: trigger.value,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true,
    },
  })

  tl.to(
    hexOne.value,
    {
      y: '0%',
      ease: Linear.easeNone,
    },
    0
  )
    .to(
      hexTwo.value,
      {
        y: '0%',
        ease: Linear.easeNone,
      },
      0
    )
    .to(
      hexThree.value,
      {
        y: '0%',
        ease: Linear.easeNone,
      },
      0
    )
})

onBeforeUnmount(() => {
  tl.kill()
})
</script>

<style lang="scss">
.benefits {
  overflow: hidden;

  .benefits-head {
    max-width: 450px;

    .text {
      margin: 20px 0 0;
    }
  }

  .hex-outline-container {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    flex-direction: column;
    width: 25%;
    height: 100%;
    transform: translate(-50%, -50%);
    z-index: -1;

    @include tablet-landscape(1) {
      width: 100%;
      top: 0;
      left: 0;
      height: 28%;
      transform: translate(0);
    }

    .hex-outline {
      position: absolute;
      display: block;
      width: 79px;
      height: 90px;
      margin-right: 12px;
      background-image: url('@/assets/img/hex-outline-small.svg');
      background-repeat: no-repeat;
      background-size: contain;

      &.one {
        top: 50px;
        left: 40%;
        width: 42px;
        height: 48px;
        transform: translateY(-100%);

        @include tablet-landscape(1) {
          top: 30px;
          left: 35%;
        }
      }

      &.two {
        right: 40px;
        top: 35%;
        width: 97px;
        height: 111px;
        transform: translateY(50%);

        @include tablet-landscape(1) {
          top: 30px;
        }
      }

      &.three {
        bottom: 0;
        left: 20px;
        width: 59px;
        height: 68px;
        transform: translateY(100%);
      }
    }
  }

  .benefits-list {
    position: relative;
    width: 1340px;
    max-width: 95%;
    padding: 90px 0 0;
    margin-left: auto;

    @media only screen and (min-width: $design-width) {
      margin: auto;
    }

    @include tablet-landscape(1) {
      padding: 30px 0 0;
      margin: auto;
    }

    .splide__slide {
      padding-right: 50px;

      @include tablet-landscape(1) {
        padding: 0;
      }
    }

    .splide__arrows {
      position: absolute;
      top: -150px;
      right: 10%;
      z-index: 1;
      display: flex;
      align-items: center;
      transform: translateY(-50%);

      @include tablet-landscape(1) {
        top: initial;
        bottom: 0;
        right: 0;
        transform: translate(0);
      }

      &--prev {
        transform: rotate(180deg);
      }

      button {
        cursor: pointer;
        width: 94px;
        height: 94px;
        padding: 0;
        opacity: 1;
        background-image: url('@/assets/img/icon-arrow.svg');
        background-repeat: no-repeat;
        background-size: contain !important;
        background-color: transparent !important;
        border: none !important;
        @include transition;

        @include mobile {
          width: 60px;
          height: 60px;
        }

        &:disabled {
          cursor: default;
          opacity: 0.7;
        }

        &.splide__arrow--prev {
          transform: rotate(180deg);
        }

        svg {
          display: none;
        }
      }
    }
  }
}
</style>
